import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { OvernightPopupCard } from "@/components/marketing/OvernightPopupCard";
import { promoPreview, useCopyOverride, usePromoActive } from "@/lib/copyOverrides";
import { gtagReportConversion } from "@/lib/gtag";
import { sendPromoEvent } from "@/lib/promoEvents";

const BLOCKED_PREFIXES = ["/book-now", "/admin", "/map5", "/pay", "/thankyou"];
const STORAGE_KEY = "sons-overnight-popup-seen";

function seenToday(): boolean {
  try {
    const today = new Date().toISOString().slice(0, 10);
    return localStorage.getItem(STORAGE_KEY) === today;
  } catch {
    return true;
  }
}

function markSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString().slice(0, 10));
  } catch {
    /* ignore */
  }
}

export function OvernightPopupModal() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const active = usePromoActive("popup");
  const delayRaw = useCopyOverride("popup.delayMs");
  const href = useCopyOverride("popup.href");

  const blocked = BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p));
  const preview = promoPreview() === "popup";

  useEffect(() => {
    if (blocked) return;
    if (!preview && (!active || seenToday())) return;
    const delay = Number.parseInt(delayRaw, 10);
    const ms = preview ? 500 : Number.isFinite(delay) && delay >= 0 ? delay : 10000;
    const timer = window.setTimeout(() => {
      setOpen(true);
      if (!preview) markSeen();
      sendPromoEvent("popup", "view");
    }, ms);
    return () => window.clearTimeout(timer);
  }, [active, blocked, delayRaw, preview]);

  useEffect(() => {
    if (!active && !preview && open) setOpen(false);
  }, [active, preview, open]);

  const handleCta = () => {
    gtagReportConversion();
    sendPromoEvent("popup", "click");
    setOpen(false);
    const target = href || "/book-now";
    if (/^https?:\/\//i.test(target)) {
      window.open(target, "_blank", "noopener,noreferrer");
    } else {
      navigate(target);
    }
  };

  if (blocked || (!active && !preview)) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <OvernightPopupCard onCta={handleCta} />
      </DialogContent>
    </Dialog>
  );
}
